<template>
    <v-card>
        <v-tabs v-model="tab" :color="moduleColor" grow show-arrows>
            <v-tab
                v-for="(orderDetail, orderDetailIndex) in orderDetails"
                :key="orderDetailIndex"
            >
                {{ orderDetail.product.Product }}
            </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
            <v-tab-item
                v-for="(orderDetail, orderDetailIndex) in orderDetails"
                :key="orderDetailIndex"
                :value="orderDetailIndex"
            >
                <MixedChart
                    :chartId="
                        'Consumptions' +
                            responseIndex +
                            orderDetail.OrderProdID.toString()
                    "
                    :chartData="chartData[orderDetailIndex]"
                    :chartOptions="chartOptions[orderDetailIndex]"
                />
            </v-tab-item>
        </v-tabs-items>
    </v-card>
</template>

<script>
import MixedChart from '../../charts/Mixed.vue';
import { dateUtils } from '../../mixins/dateUtils';

// Extend the Date prototype to add a getWeekOfYear method
Date.prototype.getWeekOfYear = function() {
    const date = new Date(this.getTime());
    // Set the date to Thursday of the current week (ISO weeks start on Monday)
    date.setHours(0, 0, 0, 0);
    // Thursday in current week decides the year
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    // January 4th is always in week 1 (ISO 8601)
    const week1 = new Date(date.getFullYear(), 0, 4);
    // Adjust to get the number of days since the start of the year
    const dayOfYear =
        ((date - week1) / 86400000 + ((week1.getDay() + 6) % 7) - 3) / 7;

    return 1 + Math.floor(dayOfYear);
};

export default {
    name: 'VolumeGraph',
    props: ['moduleColor', 'responseIndex', 'orderDetails'],
    components: {
        MixedChart
    },
    data() {
        return {
            tab: 0
        };
    },
    computed: {
        chartData() {
            return this.orderDetails.map(orderDetail =>
                this.generateChartData(orderDetail)
            );
        },
        chartOptions() {
            return this.orderDetails.map(orderDetail =>
                this.generateChartOptions(orderDetail)
            );
        },
        // Calculate the maximum value for Y1 (forecasted consumption)
        maxY1() {
            return Math.max(
                ...this.orderDetails.flatMap(orderDetail =>
                    this.normalizeAndMergeData(
                        orderDetail.readings,
                        orderDetail.rundown.simulation_data,
                        orderDetail.averageConsumptions,
                        orderDetail.ProductType
                    ).map(data => data.forecasted_consumption)
                )
            );
        },

        // Calculate the maximum value for Y2 (actual volume)
        maxY2() {
            return Math.max(
                ...this.orderDetails.flatMap(orderDetail =>
                    this.normalizeAndMergeData(
                        orderDetail.readings,
                        orderDetail.rundown.simulation_data,
                        orderDetail.averageConsumptions,
                        orderDetail.ProductType
                    ).map(data => data.total_volume)
                )
            );
        }
    },
    mixins: [dateUtils],
    mounted() {
        // Trigger the analysis when the component is mounted and data is available
        this.analyzeConsumptionSpikes();
    },
    methods: {
        // New method to analyze and emit spikes
        analyzeConsumptionSpikes() {
            const spikesByProduct = this.orderDetails.map(orderDetail => {
                const spikes = this.getConsumptionSpikes(orderDetail);
                return {
                    product: orderDetail.product.Product,
                    spikes: spikes
                };
            });

            // Emit the spikes data to the parent component
            this.$emit('consumptionSpikesDetected', spikesByProduct);
        },

        // Method to identify consumption spikes
        getConsumptionSpikes(orderDetail) {
            const combinedData = this.normalizeAndMergeData(
                orderDetail.readings,
                orderDetail.rundown.simulation_data,
                orderDetail.averageConsumptions,
                orderDetail.ProductType
            );

            // Filter days where actual consumption is more than 30% higher than forecasted
            const spikes = combinedData.filter(data => data.percentage > 30);

            // Map to desired output format
            return spikes.map(data => ({
                date: data.timestamp,
                actual_consumption: data.total_volume,
                forecasted_consumption: data.forecasted_consumption,
                difference_in_volume: data.difference,
                percentage: data.percentage.toFixed(2) + '%'
            }));
        },
        formatDateLabel(date) {
            const dayLetter = date.toLocaleDateString('en-US', {
                weekday: 'short'
            })[0];
            const dateString = date.toLocaleDateString();
            return `${dayLetter} ${dateString}`;
        },
        hourlyConsumption(consumptions, datetime, productId) {
            //IMPORTANT: FIX HARDCODED PRODUCT ID -> 166 to 78
            if (productId === 166) {
                productId = 78;
            }
            const hour = new Date(datetime).getHours(); // Extract the hour from the datetime
            const dayOfWeek = new Date(datetime).toLocaleString('en-US', {
                weekday: 'long'
            }); // Get the day of the week
            const weekOfYear = new Date(datetime).getWeekOfYear();

            // Find the matching hourly consumption and factors
            const last15DaysHourly = consumptions.find(
                c =>
                    c.product_id === productId &&
                    c.period_type === 'last-15-days-hourly' &&
                    c.period_value === hour.toString().padStart(2, '0')
            );
            const dayOfWeekFactor = consumptions.find(
                c =>
                    c.product_id === productId &&
                    c.period_type === 'day-of-week' &&
                    c.period_value === `${dayOfWeek}`
            );
            const weeklyFactor = consumptions.find(
                c =>
                    c.product_id === productId &&
                    c.period_type === 'weekly' &&
                    c.period_value === 'Week ' + weekOfYear
            );

            // Check if the required data is available
            if (!last15DaysHourly || !dayOfWeekFactor || !weeklyFactor) {
                console.error(
                    'Missing data for hourly consumption calculation',
                    last15DaysHourly,
                    dayOfWeekFactor,
                    weeklyFactor
                );
                return 0; // If any of the factors are missing, return 0
            }
            // Calculate the hourly consumption using the formula
            const last15DaysHourlyConsumption = parseFloat(
                last15DaysHourly.average_consumption
            );
            const dayFactor = parseFloat(dayOfWeekFactor.factor);
            const weekFactor = parseFloat(weeklyFactor.factor);

            let dayOfWeekScale = dayFactor * last15DaysHourlyConsumption;
            dayOfWeekScale -= last15DaysHourlyConsumption;
            let weeklyScale = weekFactor * last15DaysHourlyConsumption;
            weeklyScale -= last15DaysHourlyConsumption;

            return last15DaysHourlyConsumption + dayOfWeekScale + weeklyScale;
        },

        // Function to calculate daily consumption by aggregating hourly consumptions
        dailyConsumption(consumptions, date, productId) {
            //IMPORTANT: FIX HARDCODED PRODUCT ID -> 166 to 78
            if (productId === 166) {
                productId = 78;
            }
            let totalDailyConsumption = 0;

            // Loop through all 24 hours of the day
            for (let hour = 0; hour < 24; hour++) {
                const datetime = new Date(date);
                datetime.setHours(hour); // Set the hour for each iteration
                const hourlyConsumption = this.hourlyConsumption(
                    consumptions,
                    datetime,
                    productId
                );

                totalDailyConsumption += hourlyConsumption;
            }

            return totalDailyConsumption;
        },

        // Existing method to normalize and merge data
        normalizeAndMergeData(readings, rundownData, consumptions, productId) {
            // IMPORTANT: FIX HARDCODED PRODUCT ID -> 166 to 78
            if (productId === 166) {
                productId = 78;
            }
            const dailyData = {};
            const processedDays = new Set();

            readings.forEach((reading, index) => {
                const timestamp = new Date(reading.InventoryDttm);
                const day = timestamp.toLocaleDateString();

                if (!processedDays.has(day)) {
                    processedDays.add(day);

                    if (!dailyData[day]) {
                        dailyData[day] = {
                            consumption: 0,
                            forecasted_volume: 0,
                            forecasted_consumption: 0,
                            last_volume: reading.total_volume
                        };
                    }

                    dailyData[
                        day
                    ].forecasted_consumption = this.dailyConsumption(
                        consumptions,
                        reading.InventoryDttm,
                        productId
                    );
                }

                if (index > 0) {
                    const previousReading = readings[index - 1];
                    const previousVolume = previousReading.total_volume;
                    const currentVolume = reading.total_volume;
                    const isRefuel = currentVolume > previousVolume;

                    if (!isRefuel) {
                        const consumption = previousVolume - currentVolume;
                        dailyData[day].consumption +=
                            consumption > 0 ? consumption : 0;
                    }
                }

                dailyData[day].last_volume = reading.total_volume;
            });

            rundownData.forEach(forecast => {
                const forecastDate = new Date(
                    forecast.forecasted_timestamp
                ).toLocaleDateString();

                if (!dailyData[forecastDate]) {
                    dailyData[forecastDate] = {
                        consumption: 0,
                        forecasted_volume: 0,
                        forecasted_consumption: 0,
                        last_volume: 0
                    };
                }

                dailyData[forecastDate].forecasted_volume +=
                    forecast.forecasted_volume;
            });

            // Calculate the percentage difference and difference in volume
            return Object.keys(dailyData).map(day => {
                const {
                    consumption,
                    forecasted_volume,
                    forecasted_consumption,
                    last_volume
                } = dailyData[day];

                const difference = consumption - forecasted_consumption;
                const percentage =
                    forecasted_consumption !== 0
                        ? (difference / forecasted_consumption) * 100
                        : 0; // Handle division by zero

                return {
                    timestamp: day,
                    total_volume: consumption,
                    forecasted_volume: forecasted_volume,
                    forecasted_consumption: forecasted_consumption,
                    last_volume: last_volume,
                    difference: difference,
                    percentage: percentage
                };
            });
        },
        generateChartData(orderDetail) {
            const combinedData = this.normalizeAndMergeData(
                orderDetail.readings,
                orderDetail.rundown.simulation_data,
                orderDetail.averageConsumptions,
                orderDetail.ProductType
            );

            return {
                labels: combinedData.map(data => {
                    const date = new Date(data.timestamp);
                    return this.formatDateLabel(date); // Use 'this' if the function is in methods
                }),
                datasets: [
                    {
                        label: 'Forecasted Consumption',
                        type: 'line',
                        backgroundColor: '#24a0ed50',
                        data: combinedData.map(
                            data => data.forecasted_consumption
                        ),
                        borderColor: '#24a0ed50',
                        fill: false,
                        yAxisID: 'y1', // Link to the first y-axis
                        pointRadius: 10,
                        pointHoverRadius: 15
                    },
                    {
                        label: 'Actual Consumption',
                        type: 'line',
                        backgroundColor: '#66bb6a',
                        data: combinedData.map(data => data.total_volume),
                        borderColor: '#66bb6a',
                        fill: false,
                        yAxisID: 'y1', // Link to the first y-axis
                        pointRadius: 10,
                        pointHoverRadius: 15
                    },
                    {
                        label: 'Last Volume of the Day',
                        type: 'bar',
                        backgroundColor: '#7EE68381',
                        data: combinedData.map(data => data.last_volume),
                        borderColor: '#7EE684',
                        borderWidth: 2,
                        borderRadius: 5,
                        yAxisID: 'y2', // Link to the second y-axis
                        fill: false
                    }
                ]
            };
        },
        generateChartOptions(orderDetail) {
            const refuelEvents = orderDetail.rundown.refuel_events;

            const refuelDates = refuelEvents.map(event => {
                const date = new Date(event.timestamp);
                const dateLabel = this.formatDateLabel(date);
                return {
                    date: dateLabel,
                    eventDetails: {
                        fuelLevel: event.fuel_level, // Assuming fuelLevel is available in the event
                        arrivalTime: event.order?.StStart || 'N/A',
                        deliveredQuantity: event.order?.OrderDetails
                            ? event.order.OrderDetails.map(
                                  od => od.DeliveredQuantity
                              ).reduce((a, b) => a + b, 0)
                            : 'N/A'
                    }
                };
            });

            const lastRefuelEventDate =
                refuelDates[refuelDates.length - 1]?.date; // Get the last refuel event date

            const annotations = refuelEvents.map((event, index) => {
                const startDate = new Date(event.timestamp);
                const startDateLabel = this.formatDateLabel(startDate);

                const matchingReading = orderDetail.readings.find(reading => {
                    const readingDate = new Date(reading.InventoryDttm);
                    const readingDateLabel = this.formatDateLabel(readingDate);
                    return readingDateLabel === startDateLabel;
                });

                const fuelLevel = matchingReading
                    ? matchingReading.total_volume
                    : 0;
                const isLastAnnotation = index === refuelEvents.length - 1;

                return {
                    type: 'line',
                    scaleID: 'x', // X-axis is the one we are annotating
                    value: startDateLabel, // Ensure the date is formatted as per chart labels
                    borderColor: isLastAnnotation ? '#FF000031' : '#0000FF21',
                    borderWidth: 4,
                    label: {
                        display: ctx => ctx.hovered || ctx.focused,
                        position: 'end',
                        content: [
                            `Refuel at \n${this.formatHumanReadableDateTime(
                                startDate
                            )}`,
                            `Fuel Level: ${fuelLevel}`,
                            `Arrival Time: ${
                                event.order && event.order.StStart
                                    ? this.formatHumanReadableDateTime(
                                          event.order.StStart
                                      )
                                    : 'N/A'
                            }`,
                            `Delivered Quantity: ${
                                event.order?.OrderDetails
                                    ? event.order.OrderDetails.map(
                                          od => od.DeliveredQuantity
                                      ).reduce((a, b) => a + b, 0)
                                    : 'N/A'
                            }`
                        ],
                        backgroundColor: isLastAnnotation ? 'red' : 'blue',
                        color: 'white',
                        font: {
                            size: 15
                        },
                        padding: 2
                    },
                    enter(ctx, event) {
                        ctx.hovered = true;
                        ctx.chart.update();
                    },
                    leave(ctx, event) {
                        ctx.hovered = false;
                        ctx.chart.update();
                    },
                    click(ctx, event) {
                        ctx.focused = !ctx.focused;
                        ctx.chart.update();
                    }
                };
            });

            return {
                responsive: true,
                maintainAspectRatio: false, // Adjust as needed
                interaction: {
                    mode: 'x'
                },

                scales: {
                    x: {
                        beginAtZero: true,
                        ticks: {
                            color: context => {
                                const tickDate = context.tick.label;
                                if (
                                    refuelDates.some(
                                        refuel => refuel.date === tickDate
                                    )
                                ) {
                                    return tickDate === lastRefuelEventDate
                                        ? 'red'
                                        : 'blue';
                                }
                                return 'black';
                            }
                        }
                    },
                    y1: {
                        beginAtZero: true,
                        position: 'left',
                        title: {
                            display: true,
                            text: 'Consumption'
                        }
                    },
                    y2: {
                        beginAtZero: true,
                        position: 'right',
                        title: {
                            display: true,
                            text: 'Total Volume'
                        },
                        grid: {
                            drawOnChartArea: false // Prevent grid lines from overlapping
                        }
                    }
                },
                plugins: {
                    tooltip: {
                        enabled: true,
                        callbacks: {
                            label: context => {
                                const actualConsumption = parseInt(
                                    context.raw,
                                    10
                                );
                                const forecastedConsumption =
                                    context.dataset.label ===
                                    'Forecasted Consumption'
                                        ? actualConsumption
                                        : context.chart.data.datasets.find(
                                              ds =>
                                                  ds.label ===
                                                  'Forecasted Consumption'
                                          ).data[context.dataIndex];

                                const percentageDiff =
                                    ((actualConsumption -
                                        forecastedConsumption) /
                                        forecastedConsumption) *
                                    100;

                                const formattedPercentage = percentageDiff.toFixed(
                                    2
                                );
                                const percentageLabel = `Difference: ${formattedPercentage}% (${actualConsumption -
                                    forecastedConsumption.toFixed(0)})`;

                                if (
                                    context.dataset.label ===
                                        'Forecasted Consumption' ||
                                    context.dataset.label ===
                                        'Last Volume of the Day'
                                ) {
                                    return `${context.dataset.label}: ${actualConsumption}`;
                                } else {
                                    return `${context.dataset.label}: ${actualConsumption} (${percentageLabel})`;
                                }
                            }
                        }
                    },
                    zoom: {
                        zoom: {
                            wheel: {
                                enabled: true
                            }
                        },
                        pan: {
                            enabled: true,
                            mode: 'xy'
                        },
                        limits: {
                            y1: {
                                min: 0,
                                max: this.maxY1 + 10000
                            },
                            y2: {
                                min: 0,
                                //Get the max orderDetail.productDetails.total_capacity
                                max: this.orderDetails
                                    .map(od => od.productDetails.total_capacity)
                                    .reduce((a, b) => Math.max(a, b), 0)
                            }
                        }
                    },
                    annotation: {
                        common: {
                            drawTime: 'afterDatasetsDraw'
                        },
                        annotations: annotations // Add the refuel event lines here
                    }
                }
            };
        }
    }
};
</script>
